import { supabase } from '../lib/supabase';

/**
 * Deal Service - Handles all time-sensitive deal operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

export const dealService = {
  /**
   * Get all deals with product information
   */
  async getAllDeals() {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.select(`
          *,
          products(
            id,
            name,
            brand,
            category,
            image_url,
            image_alt
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  },

  /**
   * Get deal by ID
   */
  async getDealById(dealId) {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.select(`
          *,
          products(
            id,
            name,
            brand,
            category,
            image_url,
            image_alt,
            affiliate_links
          )
        `)
        ?.eq('id', dealId)
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching deal:', error);
      throw error;
    }
  },

  /**
   * Create new deal
   */
  async createDeal(dealData) {
    try {
      const snakeCaseData = toSnakeCase(dealData);
      
      const { data, error } = await supabase
        ?.from('deals')
        ?.insert([snakeCaseData])
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  /**
   * Update existing deal
   */
  async updateDeal(dealId, dealData) {
    try {
      const snakeCaseData = toSnakeCase(dealData);
      
      const { data, error } = await supabase
        ?.from('deals')
        ?.update({
          ...snakeCaseData,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', dealId)
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  /**
   * Delete deal
   */
  async deleteDeal(dealId) {
    try {
      const { error } = await supabase
        ?.from('deals')
        ?.delete()
        ?.eq('id', dealId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error;
    }
  },

  /**
   * Bulk update deal status
   */
  async bulkUpdateStatus(dealIds, status) {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.update({ status, updated_at: new Date()?.toISOString() })
        ?.in('id', dealIds)
        ?.select();

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error bulk updating deals:', error);
      throw error;
    }
  },

  /**
   * Extend expiration date for multiple deals
   */
  async bulkExtendExpiration(dealIds, days) {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.select('id, expiration_date')
        ?.in('id', dealIds);

      if (error) throw error;

      const updates = data?.map(deal => {
        const newExpiration = new Date(deal?.expiration_date);
        newExpiration?.setDate(newExpiration?.getDate() + days);
        
        return supabase
          ?.from('deals')
          ?.update({
            expiration_date: newExpiration?.toISOString(),
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', deal?.id);
      });

      await Promise.all(updates);

      return { success: true };
    } catch (error) {
      console.error('Error extending deal expiration:', error);
      throw error;
    }
  },

  /**
   * Track deal click
   */
  async trackClick(dealId) {
    try {
      const { error } = await supabase
        ?.rpc('increment_deal_clicks', { deal_id: dealId });

      if (error) {
        // Fallback to manual increment if RPC not available
        const { data: deal } = await supabase
          ?.from('deals')
          ?.select('click_count')
          ?.eq('id', dealId)
          ?.single();

        await supabase
          ?.from('deals')
          ?.update({ click_count: (deal?.click_count || 0) + 1 })
          ?.eq('id', dealId);
      }

      return { success: true };
    } catch (error) {
      console.error('Error tracking click:', error);
      throw error;
    }
  },

  /**
   * Get deal performance metrics
   */
  async getDealMetrics(dealId) {
    try {
      const { data, error } = await supabase
        ?.from('deals')
        ?.select('click_count, conversion_count, revenue_generated, price_history')
        ?.eq('id', dealId)
        ?.single();

      if (error) throw error;

      const metrics = toCamelCase(data);
      
      return {
        ...metrics,
        conversionRate: metrics?.clickCount > 0 
          ? ((metrics?.conversionCount / metrics?.clickCount) * 100)?.toFixed(2)
          : 0,
        averageOrderValue: metrics?.conversionCount > 0
          ? (metrics?.revenueGenerated / metrics?.conversionCount)?.toFixed(2)
          : 0
      };
    } catch (error) {
      console.error('Error fetching deal metrics:', error);
      throw error;
    }
  }
};

export default dealService;